import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useSearchParams } from 'react-router';
import { NavTabs } from '../../components/NavTabs';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState } from '../../components/States';
import { useAccess } from '../../lib/access';
import { EmbeddedPage } from '../../lib/scopedParams';
import { useHubLabels } from '../../nav/hubLabels';
import { findHub, HUBS, hubUrl, movedTarget, type HubId } from '../../nav/hubs';
import { HomeQuickActions } from './HomeQuickActions';
import { HomeToday } from './HomeToday';
import { HUB_CONTENT, hubContentKey } from './hubContent';

/** Keys the hub owns in the URL; the page inside the tab never sees them. */
const HUB_KEYS = ['tab', 'sub'] as const;

/**
 * One hub: header, tab bar (and sub-tab bar), and the open tab's page. The open tab is in the
 * URL (`?tab=…&sub=…`); tabs the user's roles cannot use are not shown. A hub with no tab the
 * user may open sends them to the first hub they can use.
 */
export function HubPage({ hubId }: { hubId: HubId }) {
  const { t } = useTranslation('nav');
  const { t: tu } = useTranslation('ux');
  const labels = useHubLabels();
  const access = useAccess();
  const [params] = useSearchParams();
  const { search, hash } = useLocation();
  const hub = findHub(hubId);
  const tabs = hub ? access.tabs(hub) : [];
  const tab = tabs.find((x) => x.id === params.get('tab')) ?? tabs[0];
  const sub = tab?.subs ? (tab.subs.find((s) => s.id === params.get('sub')) ?? tab.subs[0]) : undefined;
  const hubLabel = labels.hub(hubId);
  const tabLabel = tab ? labels.tab(hubId, tab.id) : '';
  const subLabel = tab && sub ? labels.sub(hubId, tab.id, sub.id) : '';
  useDocumentTitle([subLabel, tabLabel !== hubLabel ? tabLabel : '', hubLabel].filter(Boolean).join(' · '));

  // A tab that moved to another hub (or was a second copy of a page) opens its one place.
  const moved = hub ? movedTarget(hub.path, search) : null;
  if (moved) return <Navigate to={`${moved}${hash}`} replace />;

  if (!hub || !tab) {
    const first = HUBS.find((h) => access.tabs(h).length > 0);
    if (first && first.id !== hubId) return <Navigate to={first.path} replace />;
    return <EmptyState title={t('ui.noAccess')} />;
  }

  const crumbs = [{ label: hubLabel, to: hub.path }];
  if (tabLabel !== hubLabel) crumbs.push({ label: tabLabel, to: hubUrl(hubId, tab.id) });
  if (sub) crumbs.push({ label: subLabel, to: hubUrl(hubId, tab.id, sub.id) });
  const content = HUB_CONTENT[hubContentKey(hubId, tab.id, sub?.id)];

  return (
    <>
      <PageHeader title={hubLabel} subtitle={labels.description(hubId)} crumbs={crumbs} actions={hubId === 'home' ? <HomeQuickActions /> : undefined} />
      {hubId === 'home' && tab.id === tabs[0]?.id ? (
        <details className="mb-4 rounded-lg border bg-card">
          <summary className="cursor-pointer px-4 py-2 text-sm font-medium">{tu('today.title')}</summary>
          <div className="p-4"><HomeToday /></div>
        </details>
      ) : null}
      <NavTabs label={hubLabel} active={tab.id} tabs={tabs.map((x) => ({ id: x.id, label: labels.tab(hubId, x.id), to: hubUrl(hubId, x.id) }))} />
      {tab.subs && sub ? (
        <NavTabs level="sub" label={tabLabel} active={sub.id} tabs={tab.subs.map((s) => ({ id: s.id, label: labels.sub(hubId, tab.id, s.id), to: hubUrl(hubId, tab.id, s.id) }))} />
      ) : null}
      <EmbeddedPage key={hubContentKey(hubId, tab.id, sub?.id)} reserved={HUB_KEYS} label={subLabel || tabLabel}>
        {content}
      </EmbeddedPage>
    </>
  );
}
